<template>
  <div class="product-detail-page">
    <!-- 顶部导航栏 -->
    <div class="nav-container">
      <div class="nav-content">
        <div class="nav-left">
          <h1 class="logo">肌肤之享</h1>
          <p class="subtitle">您的专业护肤分享社区</p>
        </div>
        
        <div class="nav-center">
          <el-menu 
            mode="horizontal" 
            :router="true"
            class="nav-menu"
            :ellipsis="false"
          >
            <el-menu-item index="/">
              <el-icon><House /></el-icon>
              <span>首页</span>
            </el-menu-item>
            <el-menu-item index="/announcement">
              <el-icon><Bell /></el-icon>
              <span>系统公告</span>
            </el-menu-item>
            <el-menu-item index="/Skincare">
              <el-icon><Goods /></el-icon>
              <span>护肤产品</span>
            </el-menu-item>
            <el-menu-item index="/collection">
              <el-icon><Star /></el-icon>
              <span>我的收藏</span>
            </el-menu-item>
            <el-menu-item index="/notes">
              <el-icon><Notebook /></el-icon>
              <span>护肤笔记</span>
            </el-menu-item>
            <el-menu-item index="/knowledge">
              <el-icon><Reading /></el-icon>
              <span>护肤知识</span>
            </el-menu-item>
          </el-menu>
        </div>
        
        <div class="nav-right">
          <div v-if="isLoggedIn" class="user-actions">
            <div class="welcome-message">
              <span>欢迎，{{ userInfo.nickname || userInfo.username }}</span>
            </div>
            <div class="user-buttons">
                  <el-button size="small" type="primary" @click="router.push('/profile')">
                    <el-icon><User /></el-icon>
                    个人中心
                  </el-button>
                  <el-button size="small" type="danger" @click="handleLogout">退出登录</el-button>
                </div>
          </div>
          <div v-else class="user-actions">
            <el-button type="primary" @click="router.push('/login')">
              <el-icon><User /></el-icon>
              <span>登录</span>
            </el-button>
            <el-button @click="router.push('/login?tab=register')">
              <el-icon><Plus /></el-icon>
              <span>注册</span>
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 主要内容 -->
    <div class="product-detail-container">
      <!-- 返回按钮 -->
      <div class="back-button">
        <el-button @click="goBack">
          <el-icon><ArrowLeft /></el-icon>
          返回产品列表
        </el-button>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="loading-container">
        <el-skeleton :rows="10" animated />
      </div>

      <!-- 产品内容 -->
      <div v-else-if="productDetail" class="product-content">
        <div class="product-header">
          <div class="product-images">
            <div class="main-image">
              <el-image 
                :src="productDetail.img_url" 
                :preview-src-list="[productDetail.img_url, ...productDetail.images]"
                fit="cover"
              />
            </div>
            <div class="image-thumbnails" v-if="productDetail.images.length > 0">
              <el-image
                v-for="(img, index) in productDetail.images"
                :key="index"
                :src="img"
                fit="cover"
                class="thumbnail"
                @click="previewImage(index + 1)"
              />
            </div>
          </div>
          
          <div class="product-info">
            <div class="brand">{{ productDetail.brand }}</div>
            <h1 class="name">{{ productDetail.name }}</h1>
            
            <div class="tags">
              <el-tag 
                v-for="tag in productDetail.tags" 
                :key="tag"
                size="small"
                class="tag"
              >{{ tag }}</el-tag>
            </div>
            
            <div class="stats">
              <span class="stat-item">
                <el-icon><View /></el-icon>
                {{ productDetail.view_count }} 浏览
              </span>
              <span class="stat-item">
                <el-icon><Star /></el-icon>
                {{ productDetail.collection_count }} 收藏
              </span>
              <span class="stat-item">
                <el-icon><Pointer /></el-icon>
                {{ productDetail.like_count }} 点赞
              </span>
            </div>
            
            <div class="actions">
              <el-button 
                :type="isCollected ? 'success' : 'primary'"
                @click="toggleCollection"
                :loading="collectLoading"
              >
                <el-icon><Star /></el-icon>
                {{ isCollected ? '已收藏' : '收藏' }}
              </el-button>
              <el-button 
                :type="isLiked ? 'danger' : 'default'"
                @click="toggleLike"
                :loading="likeLoading"
              >
                <el-icon><Pointer /></el-icon>
                {{ isLiked ? '已点赞' : '点赞' }}
              </el-button>
            </div>
          </div>
        </div>

        <div class="product-body">
          <el-tabs>
            <el-tab-pane label="产品详情">
              <div class="detail-section">
                <h3>产品描述</h3>
                <p>{{ productDetail.description }}</p>
              </div>
              
              <div class="detail-section">
                <h3>适用肤质</h3>
                <div class="skin-types">
                  <el-tag
                    v-for="skin in formatSkinTypes(productDetail.suitable_skin)"
                    :key="skin"
                    size="small"
                    effect="plain"
                    class="skin-type"
                  >{{ skin }}</el-tag>
                </div>
              </div>
              
              <div class="detail-section">
                <h3>产品成分</h3>
                <p>{{ productDetail.ingredients }}</p>
              </div>
              
              <div class="detail-section">
                <h3>使用方法</h3>
                <p>{{ productDetail.usage_method }}</p>
              </div>
            </el-tab-pane>
            
            <el-tab-pane label="用户评价">
              <div class="reviews-section">
                <div class="review-stats">
                  <div class="rating">
                    <span class="rating-score">{{ avgRating }}</span>
                    <el-rate
                      v-model="avgRating"
                      disabled
                      show-score
                      text-color="#ff9900"
                    />
                  </div>
                  <div class="rating-count">共 {{ reviewCount }} 条评价</div>
                </div>
                
                <div class="review-list">
                  <!-- 评论表单 -->
                  <div v-if="isLoggedIn" class="review-form">
                    <h3>发表评价</h3>
                    <div class="form-rating">
                      <span>评分：</span>
                      <el-rate
                        v-model="commentForm.rating"
                        allow-half
                        show-score
                        text-color="#ff9900"
                      />
                    </div>
                    <el-input
                      v-model="commentForm.content"
                      type="textarea"
                      :rows="4"
                      placeholder="分享你对这个产品的看法和使用体验..."
                      :maxlength="500"
                      show-word-limit
                    />
                    <div class="form-actions">
                      <el-button 
                        type="primary" 
                        @click="submitComment"
                        :loading="commentSubmitting"
                        :disabled="!commentForm.content"
                      >
                        提交评价
                      </el-button>
                    </div>
                  </div>
                  <div v-else class="login-tip">
                    <el-alert
                      title="请先登录后发表评价"
                      type="info"
                      :closable="false"
                      show-icon
                    >
                      <template #default>
                        <el-button 
                          type="primary" 
                          size="small" 
                          @click="router.push('/login?redirect=' + encodeURIComponent(route.fullPath))"
                        >
                          去登录
                        </el-button>
                      </template>
                    </el-alert>
                  </div>
                  
                  <!-- 评价列表 -->
                  <div v-if="formattedReviews.length > 0" class="comment-list">
                    <h3>用户评价</h3>
                    <div 
                      v-for="review in formattedReviews" 
                      :key="review.id" 
                      class="comment-item"
                    >
                      <div class="comment-header">
                        <div class="user-info">
                          <el-avatar 
                            :size="40" 
                            :src="review.avatar || 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png'"
                          />
                          <div class="user-meta">
                            <div class="username">{{ review.user_nickname }}</div>
                            <div class="comment-time">{{ review.created_time }}</div>
                          </div>
                        </div>
                        <div class="rating">
                          <el-rate
                            :model-value="review.rating"
                            disabled
                            text-color="#ff9900"
                          />
                        </div>
                      </div>
                      <div class="comment-content">
                        {{ review.content }}
                      </div>
                    </div>
                    
                    <!-- 分页 -->
                    <div class="pagination-container">
                      <el-pagination
                        v-if="reviewCount > 0"
                        v-model:current-page="currentPage"
                        v-model:page-size="pageSize"
                        :page-sizes="[5, 10, 20, 50]"
                        layout="total, sizes, prev, pager, next"
                        :total="reviewCount"
                        @size-change="handleSizeChange"
                        @current-change="handleCurrentChange"
                      />
                    </div>
                  </div>
                  
                  <!-- 无数据 -->
                  <el-empty v-else description="暂无评价" />
                </div>
              </div>
            </el-tab-pane>
          </el-tabs>
        </div>
      </div>

      <!-- 错误状态 -->
      <div v-else class="error-state">
        <el-empty description="产品不存在或已下架" :image-size="200">
          <template #description>
            <p>产品不存在或已下架</p>
          </template>
        </el-empty>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import { 
  House, 
  Bell, 
  Goods, 
  Star, 
  Notebook, 
  Reading, 
  User, 
  Plus,
  // SwitchButton,
  View,
  ArrowLeft,
  Pointer
} from '@element-plus/icons-vue'
import { api } from '../utils/axios'

export default {
  name: 'ProductDetailView',
  components: {
    House,
    Bell,
    Goods,
    Star,
    Notebook,
    Reading,
    User,
    Plus,
    // SwitchButton,
    View,
    ArrowLeft,
    Pointer
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const productDetail = ref(null)
    const loading = ref(false)
    const isLoggedIn = ref(false)
    const userInfo = ref({})
    const isCollected = ref(false)
    const collectLoading = ref(false)
    const isLiked = ref(false)
    const likeLoading = ref(false)
    const avgRating = ref(5.0)
    const reviewCount = ref(0)
    const reviews = ref([])
    const currentPage = ref(1)
    const pageSize = ref(10)
    const commentForm = ref({
      rating: 5.0,
      content: ''
    })
    const commentSubmitting = ref(false)

    // 处理评论数据，确保评分是数字类型
    const formattedReviews = computed(() => {
      return reviews.value.map(review => ({
        ...review,
        rating: Number(review.rating)
      }));
    });

    // 检查登录状态
    const checkLoginStatus = () => {
      const user = localStorage.getItem('user')
      if (user) {
        userInfo.value = JSON.parse(user)
        isLoggedIn.value = true
      } else {
        isLoggedIn.value = false
        userInfo.value = {}
      }
    }

    // 检查点赞状态
    const checkLikeStatus = async () => {
      if (!isLoggedIn.value) return;
      
      try {
        const response = await api.checkLikeStatus(route.params.id, 'product');
        if (response.success) {
          isLiked.value = response.data.isLiked;
          if (productDetail.value) {
            productDetail.value.like_count = response.data.likeCount;
          }
        }
      } catch (error) {
        console.error('检查点赞状态失败:', error);
      }
    };

    // 获取产品详情
    const fetchProductDetail = async () => {
      const id = route.params.id
      if (!id) {
        ElMessage.error('产品ID不能为空')
        return
      }

      loading.value = true
      try {
        const response = await api.getProductDetail(id)
        if (response.success) {
          productDetail.value = response.data
          
          // 如果用户已登录，检查收藏和点赞状态
          if (isLoggedIn.value) {
            checkCollectionStatus()
            checkLikeStatus()
          }
        } else {
          ElMessage.error(response.message || '获取产品详情失败')
        }
      } catch (error) {
        console.error('获取产品详情失败:', error)
        ElMessage.error('获取产品详情失败，请稍后重试')
      } finally {
        loading.value = false
      }
    }

    // 检查收藏状态
    const checkCollectionStatus = async () => {
      try {
        const response = await api.checkCollection(route.params.id, 'product')
        if (response.success) {
          isCollected.value = response.data.isCollected
        }
      } catch (error) {
        console.error('检查收藏状态失败:', error)
      }
    }

    // 切换收藏状态
    const toggleCollection = async () => {
      if (!isLoggedIn.value) {
        ElMessage.warning('请先登录')
        router.push('/login?redirect=' + encodeURIComponent(route.fullPath))
        return
      }

      collectLoading.value = true
      try {
        const response = await (isCollected.value ? 
          api.uncollectItem(route.params.id, 'product') : 
          api.collectItem(route.params.id, 'product'))

        if (response.success) {
          isCollected.value = !isCollected.value
          if (isCollected.value) {
            productDetail.value.collection_count++
            ElMessage.success('收藏成功')
          } else {
            productDetail.value.collection_count--
            ElMessage.success('已取消收藏')
          }
        } else {
          ElMessage.error(response.message || '操作失败')
        }
      } catch (error) {
        console.error('收藏操作失败:', error)
        ElMessage.error('操作失败，请稍后重试')
      } finally {
        collectLoading.value = false
      }
    }

    // 切换点赞状态
    const toggleLike = async () => {
      if (!isLoggedIn.value) {
        ElMessage.warning('请先登录');
        router.push('/login?redirect=' + encodeURIComponent(route.fullPath));
        return;
      }

      likeLoading.value = true;
      try {
        const response = await api.likeItem(route.params.id, 'product');
        if (response.success) {
          isLiked.value = response.data.isLiked;
          if (productDetail.value) {
            productDetail.value.like_count = response.data.likeCount;
          }
          ElMessage.success(response.message);
        } else {
          ElMessage.error(response.message || '操作失败');
        }
      } catch (error) {
        console.error('点赞操作失败:', error);
        ElMessage.error('操作失败，请稍后重试');
      } finally {
        likeLoading.value = false;
      }
    };

    // 预览图片
    // eslint-disable-next-line no-unused-vars
    const previewImage = (index) => {
      // eslint-disable-next-line no-unused-vars
      const imageList = [productDetail.value.img_url, ...productDetail.value.images]
      // Element Plus的el-image组件会自动处理预览
    }

    // 处理退出登录
    const handleLogout = () => {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      isLoggedIn.value = false
      userInfo.value = {}
      ElMessage.success('已退出登录')
      router.push('/login')
    }

    // 返回上一页
    const goBack = () => {
      router.back()
    }

    // 获取产品评论
    const fetchProductComments = async () => {
      try {
        const response = await api.getProductComments(route.params.id, currentPage.value, pageSize.value);
        if (response.success) {
          reviews.value = response.data.list;
          reviewCount.value = parseInt(response.data.total);
          avgRating.value = parseFloat(response.data.avg_rating) || 5.0;
        }
      } catch (error) {
        console.error('获取产品评论失败:', error);
      }
    };

    // 提交评论
    const submitComment = async () => {
      if (!commentForm.value.content) {
        ElMessage.warning('请输入评论内容');
        return;
      }

      commentSubmitting.value = true;
      try {
        const response = await api.addProductComment(
          route.params.id,
          commentForm.value.content,
          Number(commentForm.value.rating)  // 确保评分是数字类型
        );

        if (response.success) {
          ElMessage.success('评价发表成功');
          // 重置表单
          commentForm.value.content = '';
          commentForm.value.rating = 5.0;
          // 刷新评论列表
          currentPage.value = 1;
          await fetchProductComments();
        } else {
          ElMessage.error(response.message || '评价发表失败');
        }
      } catch (error) {
        console.error('提交评论失败:', error);
        ElMessage.error('评价发表失败，请稍后重试');
      } finally {
        commentSubmitting.value = false;
      }
    };

    // 处理分页大小变化
    const handleSizeChange = (size) => {
      pageSize.value = size;
      currentPage.value = 1;
      fetchProductComments();
    };

    // 处理页码变化
    const handleCurrentChange = (page) => {
      currentPage.value = page;
      fetchProductComments();
    };

    // 格式化肤质类型
    const formatSkinTypes = (skinTypes) => {
      if (!skinTypes) return [];
      
      const skinTypeMap = {
        'dry': '干性肌肤',
        'oily': '油性肌肤',
        'sensitive': '敏感肌肤',
        'combination': '混合性肌肤',
        'normal': '中性肌肤',
        'all': '所有肤质'
      };
      
      return skinTypes.map(type => skinTypeMap[type] || type);
    };

    onMounted(() => {
      checkLoginStatus()
      fetchProductDetail()
      fetchProductComments()
    })

    return {
      router,
      productDetail,
      loading,
      isLoggedIn,
      userInfo,
      isCollected,
      collectLoading,
      isLiked,
      likeLoading,
      avgRating,
      reviewCount,
      reviews,
      formattedReviews,
      currentPage,
      pageSize,
      commentForm,
      commentSubmitting,
      handleLogout,
      goBack,
      toggleCollection,
      toggleLike,
      previewImage,
      submitComment,
      handleSizeChange,
      handleCurrentChange,
      formatSkinTypes,
    }
  }
}
</script>

<style scoped>
.product-detail-page {
  min-height: 100vh;
  background-color: #f5f7fa;
}

/* 导航栏样式 */
.nav-container {
  background-color: #fff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
}

.nav-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.nav-left {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 120px;
}

.logo {
  font-size: 24px;
  color: #409eff;
  margin: 0;
  font-weight: 700;
  letter-spacing: 1px;
}

.nav-left .subtitle {
  font-size: 12px;
  color: #909399;
  margin: 0;
}

.nav-menu {
  border-bottom: none;
  flex: 1;
  justify-content: center;
}

.nav-menu :deep(.el-menu-item) {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 15px;
  height: 60px;
  line-height: 60px;
}

.nav-menu :deep(.el-menu-item.is-active) {
  color: #409eff;
  font-weight: 500;
}

.nav-menu :deep(.el-menu-item .el-icon) {
  margin-right: 5px;
}

.user-actions {
  display: flex;
  gap: 10px;
  min-width: 160px;
  justify-content: flex-end;
  align-items: center;
}

.welcome-user {
  display: flex;
  align-items: center;
  gap: 15px;
}

.welcome-text {
  color: #606266;
  font-size: 14px;
  white-space: nowrap;
}

.user-buttons {
  display: flex;
  gap: 8px;
}

.user-actions .el-button {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 15px;
}

/* 页面内容样式 */
.product-detail-container {
  max-width: 1200px;
  margin: 80px auto 0;
  padding: 30px 20px 60px;
}

/* 返回按钮 */
.back-button {
  margin-bottom: 20px;
}

/* 加载状态 */
.loading-container {
  padding: 20px;
}

/* 产品内容 */
.product-content {
  background-color: #fff;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.product-header {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  margin-bottom: 40px;
}

.product-images {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.main-image {
  width: 100%;
  height: 400px;
  border-radius: 8px;
  overflow: hidden;
}

.main-image :deep(.el-image) {
  width: 100%;
  height: 100%;
}

.image-thumbnails {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 10px;
}

.thumbnail {
  width: 80px;
  height: 80px;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.3s;
}

.thumbnail:hover {
  transform: translateY(-2px);
}

.product-info {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.brand {
  font-size: 18px;
  color: #909399;
}

.name {
  font-size: 28px;
  color: #303133;
  margin: 0;
  line-height: 1.4;
}

.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag {
  font-size: 12px;
}

.stats {
  display: flex;
  gap: 20px;
  color: #909399;
  font-size: 14px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 5px;
}

.actions {
  margin-top: 20px;
}

/* 产品详情 */
.product-body {
  margin-top: 40px;
}

.detail-section {
  margin-bottom: 30px;
}

.detail-section h3 {
  font-size: 18px;
  color: #303133;
  margin: 0 0 15px;
}

.detail-section p {
  color: #606266;
  line-height: 1.8;
  margin: 0;
}

.skin-types {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.skin-type {
  font-size: 13px;
}

/* 评价部分 */
.reviews-section {
  padding: 20px 0;
}

.review-stats {
  display: flex;
  align-items: center;
  gap: 40px;
  margin-bottom: 30px;
}

.rating {
  display: flex;
  align-items: center;
  gap: 15px;
}

.rating-score {
  font-size: 36px;
  color: #ff9900;
  font-weight: bold;
}

.rating-count {
  color: #909399;
  font-size: 14px;
}

.review-form {
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
}

.review-form h3 {
  margin-top: 0;
  margin-bottom: 15px;
  font-size: 16px;
  color: #303133;
}

.form-rating {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.form-rating span {
  color: #606266;
  margin-right: 10px;
}

.form-actions {
  margin-top: 15px;
  text-align: right;
}

.login-tip {
  margin-bottom: 30px;
}

.comment-list {
  margin-top: 20px;
}

.comment-list h3 {
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 16px;
  color: #303133;
}

.comment-item {
  padding: 20px 0;
  border-bottom: 1px solid #ebeef5;
}

.comment-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
}

.user-info {
  display: flex;
  align-items: center;
}

.user-meta {
  margin-left: 10px;
}

.username {
  font-weight: 500;
  color: #303133;
  margin-bottom: 5px;
}

.comment-time {
  font-size: 12px;
  color: #909399;
}

.comment-content {
  color: #606266;
  line-height: 1.6;
  white-space: pre-wrap;
}

.pagination-container {
  margin-top: 30px;
  display: flex;
  justify-content: center;
}

/* 错误状态 */
.error-state {
  text-align: center;
  padding: 60px 0;
}

.error-state :deep(.el-empty__description p) {
  color: #909399;
  margin-bottom: 20px;
}

/* 响应式样式 */
@media (max-width: 1200px) {
  .product-header {
    grid-template-columns: 1fr;
    gap: 30px;
  }
}

@media (max-width: 900px) {
  .nav-content {
    padding: 0 10px;
  }

  .logo {
    font-size: 20px;
  }

  .nav-left .subtitle {
    display: none;
  }

  .main-image {
    height: 300px;
  }
}

@media (max-width: 768px) {
  .product-detail-container {
    padding: 20px 10px 40px;
  }

  .product-content {
    padding: 20px;
  }

  .name {
    font-size: 24px;
  }
}

@media (max-width: 480px) {
  .nav-menu {
    display: none;
  }

  .product-detail-container {
    margin-top: 60px;
  }

  .main-image {
    height: 250px;
  }

  .review-stats {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }
}
</style> 